import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { InputField, SelectField, RadioField } from '@/components/formParts';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { fetchJobOptions, fetchIndustryOptions, genderOptions, MasterOption } from '@/data';

const schemaBase = z.object({
  last_name_kanji: z.string().min(1, { message: '姓（漢字）は必須です' })
    .max(50, { message: '姓（漢字）は50文字以内で入力してください' })
    .regex(/^[\u3005\u3006\u3400-\u4DBF\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uF900-\uFAFF　]+$/, { message: 'ひらがな、カタカナ、漢字、全角スペース（1つまで）で入力してください' })
    .refine((val) => val.split('　').length <= 2, { message: '全角スペースは1つまでで入力してください' }),
  first_name_kanji: z.string().min(1, { message: '名（漢字）は必須です' })
    .max(50, { message: '名（漢字）は50文字以内で入力してください' })
    .regex(/^[\u3005\u3006\u3400-\u4DBF\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uF900-\uFAFF]+$/, { message: 'ひらがな、カタカナ、漢字で入力してください' }),
  last_name_alphabet: z.string().min(1, { message: '姓（英字）は必須です' })
    .max(50, { message: '姓（英字）は50文字以内で入力してください' })
    .regex(/^[a-zA-Z\s-]+$/, { message: '半角英字、スペース、ハイフンのみ入力してください' }),
  first_name_alphabet: z.string().min(1, { message: '名（英字）は必須です' })
    .max(50, { message: '名（英字）は50文字以内で入力してください' })
    .regex(/^[a-zA-Z\s-]+$/, { message: '半角英字、スペース、ハイフンのみ入力してください' }),
  birthday: z.string().min(1, { message: '生年月日は必須です' }),
  gender: z.string().min(1, { message: '性別は必須です' }),
  job: z.string().min(1, { message: '職業形態は必須です' }),
});

const industrySchema = z.object({
  industry: z.string().min(1, { message: '職業は必須です' }),
  industry_other: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.industry === '99' && !data.industry_other) {
    ctx.addIssue({ code: 'custom', message: 'その他を選択の場合、記入は必須です', path: ['industry_other'] });
  }
  if (data.industry === '99' && (data.industry_other ?? '').length > 100) {
    ctx.addIssue({ code: 'custom', message: 'その他の内容は100文字以内で入力してください', path: ['industry_other'] });
  }
});

const schema = schemaBase.and(industrySchema);
export type EditBasicFormData = z.infer<typeof schema>;

export type EditBasicFormHandle = {
  validateAndGet: () => Promise<{ ok: boolean; data?: EditBasicFormData }>;
};

type Props = {
  enabled: boolean;
  // 既存のpropsは互換維持（onValidationChangeはそのまま生かす）
  shouldSubmit: boolean;
  onValidationChange: (d: { isValid: boolean; formData: EditBasicFormData | undefined }) => void;
  initialValues?: Partial<EditBasicFormData>;
};

const EditBasicForm = forwardRef<EditBasicFormHandle, Props>(function EditBasicForm(
  { enabled, onValidationChange, shouldSubmit, initialValues }, ref
) {
  const DEFAULT_GENDER = 'male';   // 例: 'male'
  const DEFAULT_JOB = '1';          // 例: '1' にしたいなら '1'
  const DEFAULT_INDUSTRY = '8';     // 例: '8' にしたいなら '8'
  
  // useForm（既存）
  const { register, reset, getValues, watch, formState: { errors, isValid }, trigger, setValue } =
    useForm<EditBasicFormData>({
      mode: 'onChange',
      resolver: zodResolver(schema),
      defaultValues: {
        last_name_kanji: '', first_name_kanji: '',
        last_name_alphabet: '', first_name_alphabet: '',
        birthday: '',
        gender: DEFAULT_GENDER,   // ← ここに既定値を入れる
        job: DEFAULT_JOB,         // ← 指定IDがあれば入れてOK。未指定でも可
        industry: DEFAULT_INDUSTRY,
        industry_other: '',
      }
    });

  // 親から命令で検証＆値取得
  useImperativeHandle(ref, () => ({
    validateAndGet: async () => {
      const ok = await trigger(undefined, { shouldFocus: true });
      const data = ok ? (getValues() as EditBasicFormData) : undefined;
      onValidationChange({ isValid: ok, formData: data });
      return { ok, data };
    },
  }), [trigger, getValues, onValidationChange]);

  const [jobOptions, setJobOptions] = useState<MasterOption[]>([]);
  const [industryOptions, setIndustryOptions] = useState<MasterOption[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const [job, industry] = await Promise.all([fetchJobOptions(), fetchIndustryOptions()]);
        setJobOptions(job);
        setIndustryOptions(industry);
      } catch (e) { console.error(e); }
    })();
  }, []);
  useEffect(() => {
    if (jobOptions.length) {
      const current = (getValues('job') ?? '').toString();
      const exists = jobOptions.some(o => o.value === current);
      const fallback = DEFAULT_JOB || jobOptions[0].value;
      // options 到着時に「同じ値」をもう一度 setValue して DOM に反映させる
      setValue('job', exists ? current : fallback, { shouldValidate: true, shouldDirty: false });
    }
  }, [jobOptions, getValues, setValue, trigger]);
  
  useEffect(() => {
    if (industryOptions.length) {
      const current = (getValues('industry') ?? '').toString();
      const exists = industryOptions.some(o => o.value === current);
      const fallback = DEFAULT_INDUSTRY || industryOptions[0].value;
      setValue('industry', exists ? current : fallback, { shouldValidate: true, shouldDirty: false });
    }
  }, [industryOptions, getValues, setValue, trigger]);
  useEffect(() => {
    if (!initialValues) return;
    const norm = (s?: string) => (s ?? '').replace(/[./]/g, '-');
    reset({
      last_name_kanji:      initialValues.last_name_kanji ?? '',
      first_name_kanji:     initialValues.first_name_kanji ?? '',
      last_name_alphabet:   initialValues.last_name_alphabet ?? '',
      first_name_alphabet:  initialValues.first_name_alphabet ?? '',
      // birthday:             norm(initialValues.birthday),
      birthday:             initialValues.birthday,
      gender:               initialValues.gender ?? DEFAULT_GENDER,   // ← サーバ値が空なら既定値
      job:                  initialValues.job ?? '',                  // サーバ値が空でも、上のuseEffectで先頭を当てます
      industry:             initialValues.industry ?? '',
      industry_other:       initialValues.industry_other ?? ''
    }, { keepErrors: false });
    setTimeout(() => trigger(), 0);
  }, [initialValues, reset, trigger]);

  useEffect(() => {
    onValidationChange({ isValid, formData: isValid ? (getValues() as EditBasicFormData) : undefined });
  }, [isValid, getValues, onValidationChange]);

  // 旧shouldSubmit対応は残してもOK（不要なら削っても可）
  useEffect(() => {
    if (!shouldSubmit) return;
    (async () => {
      const ok = await trigger(undefined, { shouldFocus: true });
      onValidationChange({ isValid: ok, formData: ok ? (getValues() as EditBasicFormData) : undefined });
    })();
  }, [shouldSubmit, trigger, getValues, onValidationChange]);

  return (
    <>
      {/* 省略: フィールドは現状のまま */}
      <div className="grid grid-cols-2 gap-4">
        <InputField label="姓（漢字）" type="text" register={register('last_name_kanji')} errors={errors} hasRequiredLabel disabled={!enabled} />
        <InputField label="名（漢字）" type="text" register={register('first_name_kanji')} errors={errors} hasRequiredLabel disabled={!enabled} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <InputField label="姓（英字）" type="text" register={register('last_name_alphabet')} errors={errors} hasRequiredLabel disabled={!enabled} />
        <InputField label="名（英字）" type="text" register={register('first_name_alphabet')} errors={errors} hasRequiredLabel disabled={!enabled} />
      </div>
      <div className="xmd:max-w-[40rem]">
        <InputField label="生年月日" type="text" register={register('birthday')} errors={errors} disabled />
      </div>
      <RadioField label="性別" options={genderOptions} register={register('gender')} errors={errors} hasRequiredLabel disabled={!enabled} />
      <SelectField label="職業形態" options={jobOptions} placeholder="選択してください" register={register('job')} errors={errors} hasRequiredLabel disabled={!enabled} />
      <div className="flex flex-col gap-4">
        <SelectField label="職業" options={industryOptions} placeholder="選択してください" register={register('industry')} errors={errors} hasRequiredLabel disabled={!enabled} />
        {watch('industry') === '99' && (
          <InputField placeholder="その他の場合" type="text" register={register('industry_other')} errors={errors} hasRequiredLabel disabled={!enabled} />
        )}
      </div>
    </>
  );
});

export default EditBasicForm;
